import React, { FC, useState, useEffect, useRef } from 'react';
import { Form, FormControl, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Icon, Typography } from '@packages/ui/shared';
import { getLogger } from '@packages/core/logging';
import { createUseStyles } from 'react-jss';
import { vinSessionService } from '@web/services/singletons';
import { useNavigate } from 'react-router-dom';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { useVinSessionDispatch, useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { VIN } from '../types';
import VinDetails from './vin-details';
import { useAuthState } from '@packages/contexts/auth/auth.hooks';
import { getVin } from '../utils/vinSessionUtils';
import { ToastSuccessMessage } from './toast-success';
import { vinSessionActions } from '../context/vin-sessions.state';

type VinSessionModalProps = {
    show: boolean;
    onHide: () => void;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

/** The last list, so reopening shows rows at once while the fresh one loads. */
let lastRecentlyViewed: VIN[] | null = null;

const logger = getLogger('vin-sessions');

const useStyles = createUseStyles({
    sectionHeading: {
        margin: '0 0 4px',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#59616c',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '28px 16px',
        border: '1px solid #e3e3e3',
        borderRadius: 4,
        textAlign: 'center',
    },
    text: {
        fontSize: '15px',
        lineHeight: '24px',
        color: '#071424',
    },
    vinText: {
        fontSize: '13px',
        lineHeight: '16px',
        flexGrow: 1,
        marginRight: '8px',
    },
    vinCount: {
        minWidth: '35px',
        textAlign: 'right',
    },
    inputBox: {
        position: 'relative',
        fontSize: '15px',
        fontWeight: '400',
        lineHeight: '24px',
    },
    spinnerContainer: {
        position: 'absolute',
        right: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvent: 'none',
    },
    searchIcon: {
        cursor: 'pointer',
        position: 'absolute',
        right: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvent: 'none',
    },
    vinNumber: {
        width: 160,
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
        fontSize: 15,
        fontWeight: 400,
        color: '#007BFF',
        display: 'flex',
        alignItems: 'center',
        textTransform: 'uppercase',
    },
});

const VinSessionModal: FC<VinSessionModalProps> = (props) => {
    const { account } = useAuthState();
    const { t } = useTranslation();
    const classes = useStyles();
    const { isLoading, setIsLoading, onHide } = props;
    const navigate = useNavigate();
    const { selectedVin, toast } = useVinSessionState();
    const dispatch = useVinSessionDispatch();
    const [vin, setVin] = useState<string>('');
    const [recentlyViewed, setRecentlyViewed] = useState<VIN[]>(lastRecentlyViewed ?? []);
    const [isRecentlyViewedLoading, setIsRecentlyViewedLoading] = useState<boolean>(lastRecentlyViewed === null);
    // future implementation
    // const [activeVinSessions, setActiveVinSessions] = useState<VIN[]>([]);
    const [searchVin, setSearchVin] = useState<VIN[]>();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [recentsError, setRecentsError] = useState<boolean>(false);
    /** Counts lookups, so only the newest one is allowed to set the result. */
    const latestVinRequest = useRef<number>(0);

    useEffect(() => {
        getRecentlyViewed();
        // getActiveVinSessions();
    }, []);

    useEffect(() => {
        if (toast.toastMessage.length > 0) {
            setTimeout(() => {
                dispatch(
                    vinSessionActions.setToastMessage({
                        toast: {
                            toastMessage: '',
                            iconColor: 'white',
                            iconName: '',
                        },
                    })
                );
            }, 3000);
        }
    }, [toast]);

    async function getRecentlyViewed() {
        setRecentsError(false);
        // A cached list stays on screen while the fresh one loads, so reopening shows rows at once.
        setIsRecentlyViewedLoading(lastRecentlyViewed === null);
        try {
            const response = await vinSessionService.getRecentlyViewedVinData({
                ignoreCache: true,
                accountId: account?.accountId,
            });

            if (response.success) {
                const recentlyViewedData = response.data.map((record) => record.vehicleDetails);
                lastRecentlyViewed = recentlyViewedData;
                setRecentlyViewed(recentlyViewedData);
            }
        } catch (error) {
            logger.error('Failed to fetch the recently viewed VIN sessions', { error });
            setRecentsError(true);
        }
        setIsRecentlyViewedLoading(false);
    }

    // future implementation
    // async function getActiveVinSessions() {
    //     setIsLoading(true);
    //     try {
    //         const response = await vinSessionService.getActiveVinSessions({
    //             ignoreCache: true,
    //             accountId: account?.accountId,
    //         });

    //         if (response.success) {
    //             // setActiveVinSessions(response.data);
    //         }
    //     } catch (error) {
    //         window.alert(`Actively viewed VIN Error: ${error.message}`);
    //         setIsLoading(false);
    //     }
    //     setTimeout(() => {
    //         setIsLoading(false);
    //     }, 500);
    // }

    async function fetchVin(vin: string) {
        const request = ++latestVinRequest.current;
        const data = await getVin(vin, setIsLoading);
        // An earlier lookup that answers late must not replace what the user is looking at now.
        if (request !== latestVinRequest.current) {
            return;
        }
        if (!data || Object.keys(data).length === 0 || (Array.isArray(data) && data.length === 0)) {
            setErrorMessage('No vehicle was found for this VIN. Check the characters and try again.');
            setIsLoading(false);
            return;
        }
        setSearchVin(data);
        setIsLoading(false);
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        fetchVin(vin);
    };

    const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length > 17) {
            return;
        }
        setErrorMessage('');
        setSearchVin(undefined);
        const upperCaseVin = e.target.value.toUpperCase();
        // Only a whole VIN searches by itself. Every lookup is a heavy call, and the old
        // 8-character trigger meant typing a full VIN ran two of them.
        if (upperCaseVin.length === 17) {
            fetchVin(upperCaseVin);
        }
        setVin(upperCaseVin);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
        }
    };

    const handleClick = (vin: VIN) => {
        navigate('/vin/' + `${vin.vinRecord.vin}`);
        onHide();
    };

    return (
        <div style={{ position: 'relative' }}>
            {toast.toastMessage.length > 0 && (
                <ToastSuccessMessage
                    toastData={toast.toastMessage}
                    iconColor={toast.iconColor}
                    iconName={toast.iconName}
                    backgroundColor={toast.backgroundColor}
                />
            )}
            <Modal
                size="lg"
                show={props.show}
                centered={true}
                onHide={() => {
                    props.onHide();
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ maxHeight: '32px' }}>{t('VIN Session')}</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: 24, overflowX: 'hidden', overflowY: 'auto' }}>
                    <Form style={{ marginBottom: '16px' }}>
                        <Form.Group style={{ minHeight: 88 }}>
                            <Form.Label style={{ width: '100%' }}>
                                <div className={classes.text}>
                                    <Typography>Search by VIN</Typography>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div className={classes.vinText}>
                                        <Typography variant="caption" style={{ color: '#4C525D' }}>
                                            Enter at least the last 8 digits to get results.
                                        </Typography>
                                    </div>
                                    <div className={classes.vinCount}>
                                        <Typography variant="caption">{vin.length}/17</Typography>
                                    </div>
                                </div>
                            </Form.Label>
                            <InputGroup>
                                <FormControl
                                    className={classes.inputBox}
                                    type="text"
                                    onChange={handleVinChange}
                                    value={vin}
                                    aria-label="VIN"
                                    disabled={isLoading}
                                    onKeyDown={handleKeyDown}
                                    style={errorMessage ? { border: '#DE2622 1px solid' } : {}}
                                />
                                {isLoading ? (
                                    <div className={classes.spinnerContainer}>
                                        <Spinner animation="border" style={{ color: '#436CA1' }} />
                                    </div>
                                ) : (
                                    <div className={classes.searchIcon} data-testId="magnifying-glass">
                                        <Icon
                                            name="magnifying-glass"
                                            color="grayFive"
                                            style={{ color: 'rgb(76, 82, 93)' }}
                                            onPress={() => fetchVin(vin)}
                                        />
                                    </div>
                                )}
                            </InputGroup>
                            <Typography variant="vinError" style={{ color: '#DE2622' }}>
                                {errorMessage}
                            </Typography>
                        </Form.Group>
                    </Form>

                    {searchVin &&
                        searchVin.length > 0 &&
                        !errorMessage &&
                        searchVin.map((vin: VIN) => (
                            <VinDetails
                                key={vin.vinRecord.vin}
                                car={vin}
                                handleClick={handleClick}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                onHide={onHide}
                            />
                        ))}

                    {selectedVin && (
                        <div style={{ marginBottom: 10 }}>
                            <h2 className={classes.sectionHeading}>Current VIN session</h2>
                            <VinDetails
                                style={{ backgroundColor: '#DEE6EF', borderRadius: '4px', marginTop: 5 }}
                                car={selectedVin}
                                handleClick={handleClick}
                                // A VIN lookup must not blank the rows that are already on screen.
                                isLoading={false}
                                setIsLoading={setIsLoading}
                                onHide={onHide}
                            />
                        </div>
                    )}

                    {recentsError && !isRecentlyViewedLoading && (
                        <div className={classes.emptyState}>
                            <Typography style={{ fontWeight: '600' }}>
                                Recently viewed sessions could not be loaded
                            </Typography>
                            <Typography variant="caption" style={{ color: '#4C525D' }}>
                                Search a VIN above to start a session.
                            </Typography>
                        </div>
                    )}

                    {!recentsError && !isRecentlyViewedLoading && recentlyViewed.length === 0 && (
                        <div className={classes.emptyState}>
                            <Typography style={{ fontWeight: '600' }}>No recent VIN sessions</Typography>
                            <Typography variant="caption" style={{ color: '#4C525D' }}>
                                Search a VIN above to start one.
                            </Typography>
                        </div>
                    )}

                    {!recentsError && (isRecentlyViewedLoading || (recentlyViewed && recentlyViewed.length > 0)) && (
                        <div>
                            <h2 className={classes.sectionHeading}>Recently viewed</h2>
                            {isRecentlyViewedLoading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                                    <SkeletonLoader type="text" height={48} className="w-full" />
                                    <SkeletonLoader type="text" height={48} className="w-full" />
                                    <SkeletonLoader type="text" height={48} className="w-full" />
                                </div>
                            ) : (
                                recentlyViewed!.map((car, index: number) => (
                                    <VinDetails
                                        key={car.vinRecord.vin ?? index}
                                        car={car}
                                        handleClick={handleClick}
                                        isLoading={false}
                                        setIsLoading={setIsLoading}
                                        onHide={onHide}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default VinSessionModal;
